/**
 * The read side of a resource page: the stored resource as the flat draft
 * its form edits, one key per schema field, whether the field lives on a
 * direct property or in a simple extension.
 */
import type { Extension } from "@medplum/fhirtypes";

import type {
  ConfigDraft,
  ConfigResource,
  ConfigSchema,
} from "@/types/clinic-configuration";
import { getExtensions } from "@/lib/utils/fhir";
import { isObject } from "@/lib/utils/typeof";

import {
  getExtensionValueKey,
  isSection,
  isWritableField,
  readFieldValue,
  toSectionDraft,
} from "./configuration-schema-runtime";
import { findSharedArrayIndexes, readPropertyField } from "./resource-path";

/**
 * The fields a resource page edits as one flat form: every direct-property
 * field and every simple extension. Sections are complex extensions with
 * their own editors, so they stay out.
 *
 * Throws on two schema bugs a save would otherwise hide:
 *
 * - A key on both levels. The draft is keyed by field key, so one field
 *   would overwrite the other, or never be written.
 * - Two fields on different items of one array (see
 *   `findSharedArrayIndexes`).
 */
export const getResourceShape = (
  schema: ConfigSchema.Resource
): Record<string, ConfigSchema.PlacedField> => {
  const fields = schema.fields ?? {};
  const extensions = schema.extensions ?? {};

  for (const key of Object.keys(fields)) {
    if (key in extensions) {
      throw new Error(
        `Schema key "${key}" is both a property field and an extension.`
      );
    }
  }

  const sharedArray = findSharedArrayIndexes(Object.values(fields));
  if (sharedArray) {
    throw new Error(
      `Two fields index different items of "${sharedArray}". Clearing one would shift the other.`
    );
  }

  const simpleExtensions = Object.entries(extensions).filter(
    (entry): entry is [string, ConfigSchema.ExtensionField] =>
      !isSection(entry[1])
  );

  return { ...fields, ...Object.fromEntries(simpleExtensions) };
};

/**
 * A simple root extension's model value, found by `url` and read from the
 * `value[x]` its `valueType` names. A `list` repeats the extension, one per
 * value.
 */
const readExtensionField = (
  resource: ConfigResource,
  field: ConfigSchema.ExtensionField
): unknown => {
  if (!isWritableField(field)) {
    throw new Error(
      `Extension field "${field.slug}" is a weekSchedule, which only a section's own page can read.`
    );
  }

  const valueKey = getExtensionValueKey(field);
  const values = getExtensions(resource, field.slug).map(
    (extension) => extension[valueKey as keyof Extension]
  );

  return readFieldValue(field, field.kind === "list" ? values : values[0]);
};

/**
 * The resource as one flat draft, keyed by field key, the same shape a
 * section draft has: a missing or unreadable value gets its kind's empty
 * value, so the form always has every key.
 */
export const toResourceDraft = (
  resource: ConfigResource,
  schema: ConfigSchema.Resource
): Record<string, unknown> => {
  const shape = getResourceShape(schema);
  const model = Object.fromEntries(
    Object.entries(shape).map(([key, field]) => [
      key,
      "path" in field
        ? readPropertyField(resource, field)
        : readExtensionField(resource, field),
    ])
  );

  return toSectionDraft(shape, model);
};

/**
 * One save's input from the field groups of one resource. Each group holds
 * a different part of the same flat draft, so their drafts merge without
 * overlap; only the dirty names are written.
 */
export const collectDirtyGroups = (
  dirtySections: ConfigDraft.DirtySection[]
) => ({
  draft: Object.fromEntries(
    dirtySections.flatMap(({ section }) => {
      const groupDraft = section.getDraft();
      return isObject<Record<string, unknown>>(groupDraft)
        ? Object.entries(groupDraft)
        : [];
    })
  ),
  dirtyFieldNames: dirtySections.flatMap(({ section }) =>
    section.getDirtyFieldNames()
  ),
});

/**
 * One save's input when some of the drawer's cards are links, not schema
 * fields: each link card's draft by its section key, and the field groups
 * collected as `collectDirtyGroups` does. A link card that is not among
 * `dirtySections` is missing from `linkDrafts`. A link draft is the card's
 * own shape, unknown to the generic draft provider, so the bar types it.
 */
export const collectDirtyLinkSections = <LinkKey extends string>(
  dirtySections: ConfigDraft.DirtySection[],
  linkKeys: LinkKey[]
) => {
  const isLink = (sectionKey: string): sectionKey is LinkKey =>
    (linkKeys as string[]).includes(sectionKey);

  return {
    ...collectDirtyGroups(
      dirtySections.filter(({ sectionKey }) => !isLink(sectionKey))
    ),
    linkDrafts: Object.fromEntries(
      dirtySections.flatMap(({ sectionKey, section }) =>
        isLink(sectionKey) ? [[sectionKey, section.getDraft()]] : []
      )
    ) as Partial<Record<LinkKey, unknown>>,
  };
};
